/**
 * InlineEditField — édition click-to-edit pour les champs critiques de l'OR.
 *
 * Usage :
 *   <InlineEditField
 *     value={or.km_depose}
 *     onSave={async (v) => { await api.patch(`/or/${or.id}`, { km_depose: parseInt(v) }); }}
 *     type="number"
 *     placeholder="Ajouter"
 *     suffix=" km"
 *     formatDisplay={(v) => v ? `${v.toLocaleString('fr-CH')} km` : "—"}
 *   />
 *
 * Comportement :
 * - Click ou tap sur la valeur affichée → passe en édition (input visible)
 * - Enter ou blur → sauvegarde (appel async onSave)
 * - Escape → annule
 * - Optimistic UI + revert si onSave échoue (toast.error)
 */
import { useState, useRef, useEffect } from "react";
import { Pencil, Check, X, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function InlineEditField({
  value,
  onSave,
  type = "text",
  placeholder = "—",
  formatDisplay,
  className = "",
  inputClassName = "",
  testId,
  disabled = false,
  inputMode,
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value ?? "");
  const [saving, setSaving] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    if (!editing) setDraft(value ?? "");
  }, [value, editing]);

  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus();
      try { inputRef.current.select?.(); } catch { /* noop */ }
    }
  }, [editing]);

  const commit = async () => {
    const newVal = typeof draft === "string" ? draft.trim() : draft;
    const currentVal = value ?? "";
    if (String(newVal) === String(currentVal)) {
      setEditing(false);
      return;
    }
    setSaving(true);
    try {
      await onSave(newVal === "" ? null : newVal);
      setEditing(false);
      toast.success("Modifié");
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || "Erreur";
      toast.error(typeof msg === "string" ? msg : "Erreur de sauvegarde");
      setDraft(value ?? ""); // revert
    } finally {
      setSaving(false);
    }
  };

  const cancel = () => {
    setDraft(value ?? "");
    setEditing(false);
  };

  if (disabled) {
    return (
      <span className={className} data-testid={testId}>
        {formatDisplay ? formatDisplay(value) : (value || placeholder)}
      </span>
    );
  }

  if (!editing) {
    const display = formatDisplay ? formatDisplay(value) : (value || <span className="text-gray-300 italic">{placeholder}</span>);
    return (
      <button
        type="button"
        onClick={() => setEditing(true)}
        className={`group inline-flex items-center gap-1.5 hover:bg-amber-50 hover:text-amber-900 px-1 -mx-1 rounded transition-colors ${className}`}
        data-testid={testId}
        title="Cliquer pour modifier"
      >
        <span>{display}</span>
        <Pencil className="w-3 h-3 text-gray-300 group-hover:text-amber-600 transition-colors flex-shrink-0" />
      </button>
    );
  }

  return (
    <span className="inline-flex items-center gap-1">
      <input
        ref={inputRef}
        type={type}
        inputMode={inputMode}
        value={draft}
        disabled={saving}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") { e.preventDefault(); commit(); }
          else if (e.key === "Escape") { e.preventDefault(); cancel(); }
        }}
        onBlur={(e) => {
          // Ignore blur if target is one of our action buttons (Check/X)
          const rt = e.relatedTarget;
          if (rt && rt.dataset && rt.dataset.inlineAction) return;
          commit();
        }}
        className={`px-2 py-0.5 border border-amber-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-amber-400 ${inputClassName}`}
        data-testid={testId ? `${testId}-input` : undefined}
      />
      {saving ? (
        <Loader2 className="w-3.5 h-3.5 animate-spin text-amber-600" />
      ) : (
        <>
          <button type="button" data-inline-action="commit" onMouseDown={(e) => e.preventDefault()} onClick={commit}
            className="text-emerald-600 hover:text-emerald-800 p-0.5"
            data-testid={testId ? `${testId}-commit` : undefined} title="Valider (Entrée)">
            <Check className="w-3.5 h-3.5" />
          </button>
          <button type="button" data-inline-action="cancel" onMouseDown={(e) => e.preventDefault()} onClick={cancel}
            className="text-gray-400 hover:text-red-600 p-0.5"
            data-testid={testId ? `${testId}-cancel` : undefined} title="Annuler (Échap)">
            <X className="w-3.5 h-3.5" />
          </button>
        </>
      )}
    </span>
  );
}
